import { executeCapability } from '../utils/capabilityExecute'
import type { EeapSlot } from '../utils/eeapSlots'
import type { PayrollConfig } from '../utils/controlCenterOperationConfig'
import { DEFAULT_OPERATION_CONFIG } from '../utils/controlCenterOperationConfig'
import { invalidateCertTypesCache } from './profileService'
import type { CertTypeDef } from '../utils/certLabels'
import { parseAdvanceCategoryDefs, type AdvanceCategoryDef } from '../utils/advanceCategories'

export type { AdvanceCategoryDef }

export type PaylocityTimeUnmatched = {
  name: string
  email: string | null
  regularHours: number
  overtimeHours: number
  totalHours: number
}

export type PaylocityOmittedSalary = {
  employeeId: string
  name: string
}

export type PaylocityTimeExportOptions = {
  includeSalary?: boolean
  requireAwayFromOffice?: boolean
  omitUnapproved?: boolean
  omitUnmatched?: boolean
  hoursOnly?: boolean
  auditOvertime?: boolean
  includeAdvances?: boolean
}

export type PaylocityPerDiemRow = {
  employeeId: string
  name: string
  days: number
  amount: number
}

export type PaylocityPerDiemSkipped = {
  employeeId: string
  name: string
  reason: string
}

export type PaylocityPerDiemSummary = {
  dailyRate: number
  rows: PaylocityPerDiemRow[]
  skipped: PaylocityPerDiemSkipped[]
  totalDays: number
  totalAmount: number
}

export type PaylocityTimeEmployee = {
  employeeId: string
  name: string
  unmatched: boolean
  regularHours: number
  overtimeHours: number
  totalHours: number
  unapprovedHours: number
  perDiemDays: number
  perDiemAmount: number
}

export type PaylocityTimeUnapproved = {
  employeeId: string
  name: string
  date: string
  hours: number
}

export type PaylocityOvertimeAudit = {
  employeeId: string
  name: string
  weekStart: string
  jobtreadOvertimeHours: number
  payrollOvertimeHours: number
  totalHours: number
}

export type PaylocityTimeTotals = {
  regularHours: number
  overtimeHours: number
  totalHours: number
  unapprovedHours: number
  unapprovedEmployeeCount: number
}

export type PaylocityAdvanceDeductionCategory = {
  key: string
  label: string
}

export type PaylocityAdvanceDeductionEmployee = {
  employeeId: string
  name: string
  amounts: Record<string, number>
  total: number
}

export type PaylocityAdvanceDeductions = {
  payDate: string
  source: 'posted' | 'projected' | 'none'
  categories: PaylocityAdvanceDeductionCategory[]
  employees: PaylocityAdvanceDeductionEmployee[]
  totals: Record<string, number>
}

export type PaylocityTimeExport = {
  csv: string
  fileName: string
  startDate: string
  endDate: string
  rowCount: number
  employeeCount: number
  unmatched: PaylocityTimeUnmatched[]
  omittedSalary: PaylocityOmittedSalary[]
  employees: PaylocityTimeEmployee[]
  unapproved: PaylocityTimeUnapproved[]
  overtimeAudit: PaylocityOvertimeAudit[]
  totals: PaylocityTimeTotals
  perDiem: PaylocityPerDiemSummary
  advanceDeductions: PaylocityAdvanceDeductions
}

export type PerDiemAuditExport = {
  html: string
  fileName: string
  startDate: string
  endDate: string
  employeeCount: number
  totalDays: number
  totalAmount: number
}

export type FormCatalogField = {
  id: string
  label: string
  type: string
  groupId?: string | null
}

export type FormCatalogGroup = {
  id: string
  name: string
  mode: 'fields' | 'radio'
}

export type FormFieldCatalog = {
  formName: string
  description?: string
  status?: string
  fields: FormCatalogField[]
  groups: FormCatalogGroup[]
}

export type EeapSlotMapping = {
  targetType: 'field' | 'group'
  targetId: string
}

export type EeapFormMapping = {
  formName: string
  mappings: Record<string, EeapSlotMapping>
  slots: EeapSlot[]
  updatedAt?: string | null
}

function parseAdvanceDeductions(raw: PaylocityAdvanceDeductions | null | undefined): PaylocityAdvanceDeductions {
  const source = raw?.source === 'posted' || raw?.source === 'projected' ? raw.source : 'none'
  const categories = Array.isArray(raw?.categories)
    ? raw.categories
      .map((item) => ({
        key: String(item?.key || '').trim(),
        label: String(item?.label || item?.key || '').trim(),
      }))
      .filter((item) => item.key)
    : []
  const employees = Array.isArray(raw?.employees)
    ? raw.employees
      .map((item) => {
        const amounts: Record<string, number> = {}
        if (item?.amounts && typeof item.amounts === 'object') {
          for (const [key, value] of Object.entries(item.amounts)) {
            const n = Number(value)
            if (key && Number.isFinite(n)) amounts[key] = n
          }
        }
        return {
          employeeId: String(item?.employeeId || '').trim(),
          name: String(item?.name || item?.employeeId || ''),
          amounts,
          total: Number(item?.total) || Object.values(amounts).reduce((sum, n) => sum + n, 0),
        }
      })
      .filter((item) => item.employeeId)
    : []
  const totals: Record<string, number> = {}
  if (raw?.totals && typeof raw.totals === 'object') {
    for (const [key, value] of Object.entries(raw.totals)) {
      const n = Number(value)
      if (key && Number.isFinite(n)) totals[key] = n
    }
  }
  return {
    payDate: String(raw?.payDate || ''),
    source,
    categories,
    employees,
    totals,
  }
}

class HrToolsService {
  async listFormCatalog(): Promise<FormFieldCatalog[]> {
    const data = await executeCapability<FormFieldCatalog[]>({
      app: 'profile',
      capability: 'hr.listFormCatalog',
    })
    return Array.isArray(data) ? data : []
  }

  async getEeapFormMapping(): Promise<EeapFormMapping> {
    return executeCapability<EeapFormMapping>({
      app: 'profile',
      capability: 'hr.getEeapFormMapping',
    })
  }

  async saveEeapFormMapping(formName: string, mappings: Record<string, EeapSlotMapping>): Promise<EeapFormMapping> {
    return executeCapability<EeapFormMapping>({
      app: 'profile',
      capability: 'hr.saveEeapFormMapping',
      input: { formName, mappings },
    })
  }

  async getPayrollConfig(): Promise<PayrollConfig> {
    const data = await executeCapability<PayrollConfig>({
      app: 'profile',
      capability: 'hr.getPayrollConfig',
    })
    return {
      otMethod: data?.otMethod ?? DEFAULT_OPERATION_CONFIG.otMethod,
      weeklyThresholdHours: data?.weeklyThresholdHours ?? DEFAULT_OPERATION_CONFIG.weeklyThresholdHours,
      dailyThresholdHours: data?.dailyThresholdHours ?? DEFAULT_OPERATION_CONFIG.dailyThresholdHours,
      weekStartsOn: data?.weekStartsOn ?? DEFAULT_OPERATION_CONFIG.weekStartsOn,
      payPeriodType: data?.payPeriodType ?? DEFAULT_OPERATION_CONFIG.payPeriodType,
      biweeklyAnchorDate: data?.biweeklyAnchorDate ?? null,
      payDateWeekday: data?.payDateWeekday ?? DEFAULT_OPERATION_CONFIG.payDateWeekday,
    }
  }

  async savePayrollConfig(input: PayrollConfig): Promise<PayrollConfig> {
    return executeCapability<PayrollConfig>({
      app: 'profile',
      capability: 'hr.savePayrollConfig',
      input,
    })
  }

  async getEeapCategories(): Promise<string[]> {
    const data = await executeCapability<{ categories?: string[] }>({
      app: 'profile',
      capability: 'hr.getEeapCategories',
    })
    return Array.isArray(data?.categories) ? data.categories.filter(Boolean) : []
  }

  async saveEeapCategories(categories: string[]): Promise<string[]> {
    const data = await executeCapability<{ categories?: string[] }>({
      app: 'profile',
      capability: 'hr.saveEeapCategories',
      input: { categories },
    })
    return Array.isArray(data?.categories) ? data.categories.filter(Boolean) : []
  }

  async getPositions(): Promise<string[]> {
    const data = await executeCapability<{ positions?: string[] }>({
      app: 'profile',
      capability: 'hr.getPositions',
    })
    return Array.isArray(data?.positions) ? data.positions.map((item) => String(item || '').trim()).filter(Boolean) : []
  }

  async savePositions(positions: string[]): Promise<string[]> {
    const data = await executeCapability<{ positions?: string[] }>({
      app: 'profile',
      capability: 'hr.savePositions',
      input: { positions },
    })
    return Array.isArray(data?.positions) ? data.positions.map((item) => String(item || '').trim()).filter(Boolean) : []
  }

  async getCertTypes(): Promise<CertTypeDef[]> {
    const data = await executeCapability<{ types?: CertTypeDef[] }>({
      app: 'profile',
      capability: 'hr.getCertTypes',
    })
    return Array.isArray(data?.types)
      ? data.types
          .map((item) => ({
            key: String(item?.key || '').trim(),
            label: String(item?.label || '').trim(),
          }))
          .filter((item) => item.key && item.label)
      : []
  }

  async saveCertTypes(types: CertTypeDef[]): Promise<CertTypeDef[]> {
    const data = await executeCapability<{ types?: CertTypeDef[] }>({
      app: 'profile',
      capability: 'hr.saveCertTypes',
      input: { types },
    })
    invalidateCertTypesCache()
    return Array.isArray(data?.types)
      ? data.types
          .map((item) => ({
            key: String(item?.key || '').trim(),
            label: String(item?.label || '').trim(),
          }))
          .filter((item) => item.key && item.label)
      : []
  }

  async getAdvanceCategories(): Promise<AdvanceCategoryDef[]> {
    const data = await executeCapability<{ categories?: AdvanceCategoryDef[] }>({
      app: 'profile',
      capability: 'hr.getAdvanceCategories',
    })
    return parseAdvanceCategoryDefs(data?.categories)
  }

  async saveAdvanceCategories(categories: AdvanceCategoryDef[]): Promise<AdvanceCategoryDef[]> {
    const data = await executeCapability<{ categories?: AdvanceCategoryDef[] }>({
      app: 'profile',
      capability: 'hr.saveAdvanceCategories',
      input: { categories },
    })
    return parseAdvanceCategoryDefs(data?.categories)
  }

  async exportPaylocityTime(
    startDate: string,
    endDate: string,
    options: PaylocityTimeExportOptions = {},
  ): Promise<PaylocityTimeExport> {
    const data = await executeCapability<PaylocityTimeExport>({
      app: 'profile',
      capability: 'hr.exportPaylocityTime',
      input: {
        startDate,
        endDate,
        includeSalary: options.includeSalary === true,
        requireAwayFromOffice: options.requireAwayFromOffice === true,
        omitUnapproved: options.omitUnapproved === true,
        omitUnmatched: options.omitUnmatched === true,
        hoursOnly: options.hoursOnly === true,
        auditOvertime: options.auditOvertime !== false,
        includeAdvances: options.includeAdvances === true,
      },
    })
    return {
      csv: String(data?.csv || ''),
      fileName: String(data?.fileName || `paylocity-time-${startDate}-to-${endDate}.csv`),
      startDate: String(data?.startDate || startDate),
      endDate: String(data?.endDate || endDate),
      rowCount: Number(data?.rowCount) || 0,
      employeeCount: Number(data?.employeeCount) || 0,
      unmatched: Array.isArray(data?.unmatched) ? data.unmatched : [],
      omittedSalary: Array.isArray(data?.omittedSalary) ? data.omittedSalary : [],
      employees: Array.isArray(data?.employees) ? data.employees : [],
      unapproved: Array.isArray(data?.unapproved) ? data.unapproved : [],
      overtimeAudit: Array.isArray(data?.overtimeAudit) ? data.overtimeAudit : [],
      totals: {
        regularHours: Number(data?.totals?.regularHours) || 0,
        overtimeHours: Number(data?.totals?.overtimeHours) || 0,
        totalHours: Number(data?.totals?.totalHours) || 0,
        unapprovedHours: Number(data?.totals?.unapprovedHours) || 0,
        unapprovedEmployeeCount: Number(data?.totals?.unapprovedEmployeeCount) || 0,
      },
      perDiem: {
        dailyRate: Number(data?.perDiem?.dailyRate) || 40,
        rows: Array.isArray(data?.perDiem?.rows) ? data.perDiem.rows : [],
        skipped: Array.isArray(data?.perDiem?.skipped) ? data.perDiem.skipped : [],
        totalDays: Number(data?.perDiem?.totalDays) || 0,
        totalAmount: Number(data?.perDiem?.totalAmount) || 0,
      },
      advanceDeductions: parseAdvanceDeductions(data?.advanceDeductions),
    }
  }

  async exportPerDiemAudit(
    startDate: string,
    endDate: string,
    options: Pick<PaylocityTimeExportOptions, 'includeSalary' | 'requireAwayFromOffice'> = {},
  ): Promise<PerDiemAuditExport> {
    const data = await executeCapability<PerDiemAuditExport>({
      app: 'profile',
      capability: 'hr.exportPerDiemAudit',
      input: {
        startDate,
        endDate,
        includeSalary: options.includeSalary === true,
        requireAwayFromOffice: options.requireAwayFromOffice === true,
      },
    })
    return {
      html: String(data?.html || ''),
      fileName: String(data?.fileName || `per-diem-audit-${startDate}-to-${endDate}.html`),
      startDate: String(data?.startDate || startDate),
      endDate: String(data?.endDate || endDate),
      employeeCount: Number(data?.employeeCount) || 0,
      totalDays: Number(data?.totalDays) || 0,
      totalAmount: Number(data?.totalAmount) || 0,
    }
  }
}

export const hrToolsService = new HrToolsService()
